import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ArrowLeft } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import RiskProfileFiltersGrid from '../components/RiskProfileFiltersGrid'
import { EconomicAnalysis } from '../components/profile-analysis/EconomicAnalysis/EconomicAnalysis'
import { GroupAnalysis } from '../components/profile-analysis/GroupAnalysis/GroupAnalysis'
import { SocialAnalysis } from '../components/profile-analysis/SocialAnalysis/SocialAnalysis'
import { TypeAnalysis } from '../components/profile-analysis/TypeAnalysis/TypeAnalysis'
import { useElementBuckets } from '../hooks/useElementBuckets'
import { useElementGroupBuckets } from '../hooks/useElementGroupBuckets'
import { useProfileAnalysisFilters } from '../hooks/useProfileAnalysisFilters'
import { getPageTitle } from '../utils/riskProfile.utils'
import {
  AnalysisMode,
  CapitalType,
  DEFAULT_ANALYSIS_MODE,
  DEFAULT_CAPITAL_TYPE,
} from '../constants/riskProfile.constants'
import { FEATURE_FLAGS } from '@app/app-constants'
import { useFeatureFlags } from '@app/hooks/useFeatureFlags'
import { useEffect } from 'react'
import { useRiskFilters } from '../../shared/hooks/useRiskFilters'

const isAnalysisMode = (value: string | null): value is AnalysisMode =>
  value === 'by-group' || value === 'by-type'

const isCapitalType = (value: string | null): value is CapitalType =>
  value === 'built' || value === 'social' || value === 'economic'

export default function ProfileAnalysisPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { selectedWorkspace, areaGroup, areas, selectedElementGroups } = useProfileAnalysisFilters()
  const { useFeatureFlag } = useFeatureFlags()
  const hasSocialContent = useFeatureFlag(FEATURE_FLAGS.PROFILE_SOCIAL_CONTENT)
  const hasEconomicContent = useFeatureFlag(FEATURE_FLAGS.PROFILE_ECONOMIC_CONTENT)
  const { setAnalysisMode } = useRiskFilters()
  const capitalTypeParam = searchParams.get('capitalType')
  const capitalType: CapitalType = isCapitalType(capitalTypeParam)
    ? capitalTypeParam
    : DEFAULT_CAPITAL_TYPE

  const analysisParam = searchParams.get('analysis')
  const analysisMode: AnalysisMode = isAnalysisMode(analysisParam)
    ? analysisParam
    : DEFAULT_ANALYSIS_MODE

  const setCapitalType = (nextCapitalType: string) => {
    if (!isCapitalType(nextCapitalType)) return

    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams)
        nextParams.set('capitalType', nextCapitalType)

        if (nextCapitalType !== 'built') {
          nextParams.delete('analysis')
        }

        return nextParams
      },
      { replace: true },
    )
  }

  const groupBuckets = useElementGroupBuckets(selectedElementGroups)
  const typeBuckets = useElementBuckets(selectedElementGroups)

  useEffect(() => {
    const isUnavailableCapitalType =
      (capitalType === 'social' && !hasSocialContent) ||
      (capitalType === 'economic' && !hasEconomicContent)

    if (!isUnavailableCapitalType) return

    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams)
        nextParams.set('capitalType', 'built')

        return nextParams
      },
      { replace: true },
    )
  }, [capitalType, hasSocialContent, hasEconomicContent, setSearchParams])

  const selectedBuckets = analysisMode === 'by-group' ? groupBuckets : typeBuckets

  const pageTitle =
    capitalType === 'built' && selectedWorkspace !== null
      ? getPageTitle({
          areaGroup,
          areas,
          analysisMode,
          selectedBuckets,
        })
      : null

  return (
    <div className="h-full overflow-y-auto bg-neutral-50">
      <RiskProfileFiltersGrid />
      <main className="mx-auto min-h-[520px] max-w-7xl px-2 py-8 lg:px-0">
        <Tabs value={capitalType} onValueChange={setCapitalType}>
          <TabsList>
            <TabsTrigger value="built">Built</TabsTrigger>
            {hasSocialContent && <TabsTrigger value="social">Social</TabsTrigger>}
            {hasEconomicContent && <TabsTrigger value="economic">Economic</TabsTrigger>}
          </TabsList>
        </Tabs>

        {pageTitle !== null && (
          <header className="mb-8">
            <h1 className="text-3xl mt-4 font-semibold tracking-tight text-foreground">
              {pageTitle}
            </h1>
          </header>
        )}

        {capitalType === 'built' && (
          <Tabs value={analysisMode} onValueChange={setAnalysisMode} className="space-y-5">
            <TabsList>
              <TabsTrigger value="by-group">By group</TabsTrigger>
              <TabsTrigger value="by-type">By type</TabsTrigger>
            </TabsList>
            <TabsContent value="by-group">
              <GroupAnalysis />
            </TabsContent>
            <TabsContent value="by-type">
              <TypeAnalysis />
            </TabsContent>
          </Tabs>
        )}

        {hasSocialContent && capitalType === 'social' && <SocialAnalysis />}
        {hasEconomicContent && capitalType === 'economic' && <EconomicAnalysis />}
      </main>
    </div>
  )
}
